import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/use-user';
import { toast } from 'sonner';
import { Download, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';
import CombinedImagePreviewModal from './CombinedImagePreviewModal';

interface CanvasElement {
  id: string;
  type: 'image' | 'text' | 'shape';
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  zIndex: number;
}

interface ProductPreviewProps {
  designImageUrl?: string;
  designElements?: CanvasElement[];
  storeId?: string;
}

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ProductDisplay = styled.div`
  position: relative;
  aspect-ratio: 1;
  background: #f8fafc;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const DesignArea = styled.div`
  position: absolute;
  top: 25%;
  left: 50%;
  width: 35%;
  height: 35%;
  transform: translateX(-50%);
  border: 2px dashed rgba(59, 130, 246, 0.3);
  background: rgba(59, 130, 246, 0.05);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const DesignOverlay = styled.div<{ 
  x: number; 
  y: number; 
  scale: number; 
  rotation: number;
  isDragging?: boolean 
}>`
  position: absolute;
  top: ${props => 40 + props.y}%;
  left: ${props => 50 + props.x}%;
  width: ${props => 30 * props.scale}%;
  height: ${props => 30 * props.scale}%;
  z-index: 10;
  transform: translate(-50%, -50%) rotate(${props => props.rotation}deg);
  transition: ${props => props.isDragging ? 'none' : 'all 0.1s ease'};
  cursor: move;
  border: 2px solid rgba(59, 130, 246, 0);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    border-color: #3b82f600;
  }
`;

const DesignImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: fill;
  user-select: none;
  pointer-events: none;
`;

const DesignControls = styled.div`
  position: absolute;
  top: 5%;
  left: 50%;
  transform: translate(-50%, -200%);
  display: flex;
  gap: 0.5rem;
  z-index: 20;
`;

const ControlButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  color: #1f2937;
  
  &:hover {
    background: white;
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
  
  @media (prefers-color-scheme: dark) {
    background: rgba(30, 30, 30, 0.95);
    border-color: #374151;
    color: white;
    
    &:hover {
      background: #1f2937;
    }
  }
  
  [data-theme="dark"] & {
    background: rgba(30, 30, 30, 0.95);
    border-color: #374151;
    color: white;
    
    &:hover {
      background: #1f2937;
    }
  }
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ProductTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const ProductPrice = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #059669;
`;

const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
`;

const ColorOptions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const ColorButton = styled.button<{ color: string; selected: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 3px solid ${props => props.selected ? '#3B82F6' : '#e5e7eb'};
  background-color: ${props => props.color};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const SaveButton = styled(Button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  
  &:hover {
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  }
`;

const CombineButton = styled(Button)`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  font-weight: 600;
  
  &:hover {
    background: linear-gradient(135deg, #047857 0%, #065f46 100%);
  }
`;

const GenderTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const GenderTab = styled.button<{ active: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid ${props => props.active ? '#1f2937' : '#e5e7eb'};
  background: ${props => props.active ? '#1f2937' : 'white'};
  color: ${props => props.active ? 'white' : '#1f2937'};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? '#374151' : '#f9fafb'};
  }
`;

const LidoProductPreview: React.FC<ProductPreviewProps> = ({ 
  designImageUrl, 
  designElements = [],
  storeId 
}) => {
  const [selectedProduct, setSelectedProduct] = useState('tshirt');
  const [selectedColor, setSelectedColor] = useState('black');
  const [selectedGender, setSelectedGender] = useState('male');
  const [isSaving, setIsSaving] = useState(false);
  const [isCombining, setIsCombining] = useState(false);
  const [designPosition, setDesignPosition] = useState({ x: 0, y: 0 });
  const [designScale, setDesignScale] = useState(1);
  const [designRotation, setDesignRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [combinedImageUrl, setCombinedImageUrl] = useState<string>('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { user } = useUser();

  const products = {
    tshirt: {
      name: 'Camiseta',
      price: 29.90,
      category: 'tshirts',
      images: {
        male: {
          black: '/lovable-uploads/46e865d0-1cdc-453f-9215-5561015fddd9.png',
          white: '/lovable-uploads/02eed271-a26e-4f86-944c-4a1f105a808f.png',
          gray: '/lovable-uploads/d83cc574-d67c-40c0-bdcf-f0dc3a3f63e3.png',
          yellow: '/lovable-uploads/4833d3ce-dea7-4e3f-8873-69b9dd84d83a.png',
          red: '/lovable-uploads/3f83fbd8-f0af-43bf-b20e-e0a14f5bc74d.png',
          green: '/lovable-uploads/70093618-f138-42f5-a1fe-9fdd08eff1f2.png'
        },
        female: {
          black: '/lovable-uploads/2c37ef97-9a38-4703-b28a-3c4750687911.png',
          white: '/lovable-uploads/d42bac06-5d60-4163-bdaa-2bcd092ca078.png',
          gray: '/lovable-uploads/6eb6bf84-e85d-4e64-b618-5aec0c3aa718.png'
        }
      }
    },
    hoodie: {
      name: 'Moletom',
      price: 79.90,
      category: 'hoodies',
      images: {
        male: {
          black: '/lovable-uploads/cd49ccec-c709-4bac-8e2f-4525a15b5bb2.png',
          white: '/lovable-uploads/72ccccf3-0e5a-405e-a8d5-9f8ed5a80972.png',
          gray: '/lovable-uploads/685512b7-8846-4d68-8636-0f34fe4ec6a4.png'
        },
        female: {
          black: '/lovable-uploads/16ab43cb-18b9-43cd-a0f9-133b42a657a9.png',
          white: '/lovable-uploads/0b5f0d6b-da14-44a2-b280-50a77374ba20.png',
          gray: '/lovable-uploads/9625e2ef-8588-46ad-81bd-9742398e5f71.png'
        }
      }
    }
  };

  const colors = [
    { id: 'black', name: 'Preto', hex: '#000000' },
    { id: 'white', name: 'Branco', hex: '#ffffff' },
    { id: 'gray', name: 'Cinza', hex: '#808080' },
    { id: 'yellow', name: 'Amarelo', hex: '#fbbf24' },
    { id: 'red', name: 'Vermelho', hex: '#dc2626' },
    { id: 'green', name: 'Verde', hex: '#059669' }
  ];

  const currentProduct = products[selectedProduct as keyof typeof products];
  const currentGenderImages = currentProduct?.images[selectedGender as keyof typeof currentProduct.images];
  const currentImage = currentGenderImages?.[selectedColor as keyof typeof currentGenderImages];

  // Filtrar cores disponíveis baseado no produto e gênero selecionados
  const availableColors = colors.filter(color => {
    const hasImage = currentGenderImages?.[color.id as keyof typeof currentGenderImages];
    return hasImage;
  });

  // Reset color selection if not available for current product/gender
  useEffect(() => {
    if (!availableColors.find(color => color.id === selectedColor)) {
      setSelectedColor(availableColors[0]?.id || 'black');
    }
  }, [selectedProduct, selectedGender, availableColors, selectedColor]);

  const handleDesignDrag = (e: React.MouseEvent) => {
    if (!designImageUrl || !isDragging) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const x = ((e.clientX - rect.left - centerX) / rect.width) * 100;
    const y = ((e.clientY - rect.top - centerY) / rect.height) * 100;
    
    const constrainedX = Math.max(-15, Math.min(15, x));
    const constrainedY = Math.max(-10, Math.min(10, y));
    
    setDesignPosition({ x: constrainedX, y: constrainedY });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      handleDesignDrag(e);
    }
  };

  const handleScaleUp = () => {
    setDesignScale(prev => Math.min(2, prev + 0.1));
  };

  const handleScaleDown = () => {
    setDesignScale(prev => Math.max(0.3, prev - 0.1));
  };

  const handleRotate = () => {
    setDesignRotation(prev => (prev + 15) % 360);
  };

  const ensureBucketExists = async (bucketName: string) => {
    try {
      // Primeiro tentar listar o conteúdo do bucket para verificar se existe
      const { data: testData, error: testError } = await supabase.storage
        .from(bucketName)
        .list('', { limit: 1 });

      if (testError && testError.message.includes('Bucket not found')) {
        console.log(`Bucket ${bucketName} não encontrado, tentando criar...`);
        
        // Tentar criar o bucket
        const { data: createData, error: createError } = await supabase.storage.createBucket(bucketName, {
          public: true,
          allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
          fileSizeLimit: 52428800 // 50MB
        });

        if (createError) {
          console.error('Erro ao criar bucket:', createError);
          
          // Se falhar ao criar, tentar usar o bucket user-images como fallback
          console.log('Usando bucket user-images como fallback...');
          return 'user-images';
        }

        console.log('Bucket criado com sucesso:', createData);
        return bucketName;
      } else if (testError) {
        console.error('Erro ao testar bucket:', testError);
        // Usar bucket user-images como fallback
        return 'user-images';
      }

      console.log(`Bucket ${bucketName} já existe`);
      return bucketName;
    } catch (error) {
      console.error('Erro ao verificar/criar bucket:', error);
      // Usar bucket user-images como fallback
      return 'user-images';
    }
  };

  const combineImages = async (): Promise<string> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx || !currentImage || !designImageUrl) {
        reject('Canvas context not available or images missing');
        return;
      }

      canvas.width = 800;
      canvas.height = 800;

      const productImg = new Image();
      const designImg = new Image();
      
      let loadedImages = 0;
      const checkComplete = () => {
        loadedImages++;
        if (loadedImages === 2) {
          try {
            // Draw product image
            ctx.drawImage(productImg, 0, 0, canvas.width, canvas.height);
            
            // Calculate design position and size - centralizado na área de impressão
            const designAreaCenterX = canvas.width * 0.5;
            const designAreaCenterY = canvas.width * 0.42; // Ajustado para melhor centralização
            
            const designWidth = (canvas.width * 0.30 * designScale);
            const designHeight = (canvas.height * 0.30 * designScale);
            
            const designX = designAreaCenterX + (designPosition.x * canvas.width * 0.008);
            const designY = designAreaCenterY + (designPosition.y * canvas.height * 0.008);
            
            // Draw design with rotation
            ctx.save();
            ctx.translate(designX, designY);
            ctx.rotate((designRotation * Math.PI) / 180);
            ctx.drawImage(designImg, -designWidth / 2, -designHeight / 2, designWidth, designHeight);
            ctx.restore();
            
            canvas.toBlob((blob) => {
              if (blob) {
                const url = URL.createObjectURL(blob);
                resolve(url);
              } else {
                reject('Failed to create blob');
              }
            }, 'image/png');
          } catch (error) {
            reject(error);
          }
        }
      };
      
      productImg.onload = checkComplete;
      designImg.onload = checkComplete;
      
      productImg.onerror = () => reject('Failed to load product image');
      designImg.onerror = () => reject('Failed to load design image');
      
      productImg.crossOrigin = 'anonymous';
      designImg.crossOrigin = 'anonymous';
      
      productImg.src = currentImage;
      designImg.src = designImageUrl;
    });
  };

  const uploadCombinedImage = async (imageBlob: Blob): Promise<string> => {
    if (!user || !storeId) {
      throw new Error('Usuário ou loja não encontrado');
    }

    const fileName = `combined-${Date.now()}-${selectedProduct}-${selectedGender}-${selectedColor}.png`;
    const filePath = `${user.id}/${storeId}/combined/${fileName}`;
    
    console.log('Uploading to:', { bucket: 'combined-images', filePath });
    
    const { data, error } = await supabase.storage
      .from('combined-images')
      .upload(filePath, imageBlob, {
        contentType: 'image/png',
        upsert: false
      });

    if (error) {
      console.error('Upload error:', error);
      throw new Error(`Erro no upload: ${error.message}`);
    }

    console.log('Upload successful:', data);

    const { data: publicUrlData } = supabase.storage
      .from('combined-images')
      .getPublicUrl(filePath);

    return publicUrlData.publicUrl;
  };

  const handleCombineAndPreview = async () => {
    if (!designImageUrl || !user || !storeId) {
      toast.error('É necessário ter um design e estar logado');
      return;
    }

    setIsCombining(true);

    try {
      console.log('Starting image combination...');
      const combinedUrl = await combineImages();
      
      console.log('Converting to blob...');
      const response = await fetch(combinedUrl);
      const blob = await response.blob();
      
      console.log('Uploading to storage...');
      const uploadedUrl = await uploadCombinedImage(blob);
      
      setCombinedImageUrl(uploadedUrl);
      setShowPreviewModal(true);
      toast.success('Imagem combinada com sucesso!');
      
    } catch (error) {
      console.error('Erro ao combinar imagens:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`Erro ao combinar as imagens: ${errorMessage}`);
    } finally {
      setIsCombining(false);
    }
  };

  const handleSaveProduct = async () => {
    if (!combinedImageUrl || !user || !storeId) {
      toast.error('É necessário gerar a imagem combinada primeiro');
      return;
    }

    setIsSaving(true);

    try {
      const { data: storeData } = await supabase
        .from('user_stores')
        .select('store_name')
        .eq('id', storeId)
        .single();

      if (!storeData) {
        toast.error('Loja não encontrada');
        return;
      }

      const mockupImages = {
        [selectedProduct]: currentProduct.images
      };

      const { data, error } = await supabase
        .from('store_products')
        .insert({
          title: `${currentProduct.name} ${selectedGender === 'male' ? 'Masculina' : 'Feminina'} - Design Personalizado`,
          description: `${currentProduct.name} ${selectedGender === 'male' ? 'masculina' : 'feminina'} com design personalizado aplicado`,
          price: currentProduct.price,
          category: currentProduct.category,
          user_id: user.id,
          design_image_url: designImageUrl,
          mockup_images: mockupImages,
          store_name: storeData.store_name,
          images: [combinedImageUrl],
          available_colors: availableColors.map(color => color.name),
          available_sizes: ['P', 'M', 'G', 'GG'],
          print_options: ['Frente', 'Costas', 'Ambos'],
          is_active: true
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao salvar produto:', error);
        toast.error('Erro ao salvar produto na loja');
        return;
      }

      toast.success('Produto salvo com sucesso na Ink Shop!');
      setShowPreviewModal(false);
      
    } catch (error) {
      console.error('Erro ao salvar produto:', error);
      toast.error('Erro ao salvar produto');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditDesign = () => {
    // Apenas fecha o modal, mantendo o design atual para edição
    setShowPreviewModal(false);
  };

  return (
    <PreviewContainer>
      <ProductDisplay
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {currentImage ? (
          <>
            <ProductImage src={currentImage} alt={`${currentProduct.name} ${selectedGender} ${selectedColor}`} />

            {!designImageUrl && (
              <DesignArea>
                <div style={{ 
                  textAlign: 'center', 
                  color: '#6b7280', 
                  fontSize: '0.875rem',
                  padding: '1rem'
                }}>
                  Área da estampa
                  <br />
                  Adicione sua arte
                </div>
              </DesignArea>
            )}

            {designImageUrl && (
              <>
                <DesignOverlay
                  x={designPosition.x}
                  y={designPosition.y}
                  scale={designScale}
                  rotation={designRotation}
                  isDragging={isDragging}
                  onMouseDown={handleMouseDown}
                >
                  <DesignImage 
                    src={designImageUrl} 
                    alt="Design aplicado"
                    draggable={false}
                  />
                  
                  <DesignControls>
                    <ControlButton onClick={handleScaleUp} title="Aumentar">
                      <ZoomIn size={14} />
                    </ControlButton>
                    <ControlButton onClick={handleScaleDown} title="Diminuir">
                      <ZoomOut size={14} />
                    </ControlButton>
                    <ControlButton onClick={handleRotate} title="Rotacionar">
                      <RotateCw size={14} />
                    </ControlButton>
                  </DesignControls>
                </DesignOverlay>
              </>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6b7280' }}>
            Selecione um produto para visualizar
          </div>
        )}
      </ProductDisplay>

      <ProductInfo>
        <ProductTitle>{currentProduct?.name} {selectedGender === 'male' ? 'Masculina' : 'Feminina'}</ProductTitle>
        <ProductPrice>R$ {currentProduct?.price.toFixed(2)}</ProductPrice>

        <ControlGroup>
          <Label>Tipo de Produto</Label>
          <Select value={selectedProduct} onValueChange={setSelectedProduct}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tshirt">Camiseta - R$ 29,90</SelectItem>
              <SelectItem value="hoodie">Moletom - R$ 79,90</SelectItem>
            </SelectContent>
          </Select>
        </ControlGroup>

        <ControlGroup>
          <Label>Gênero</Label>
          <GenderTabs>
            <GenderTab 
              active={selectedGender === 'male'} 
              onClick={() => setSelectedGender('male')}
            >
              Masculino
            </GenderTab>
            <GenderTab 
              active={selectedGender === 'female'} 
              onClick={() => setSelectedGender('female')}
            >
              Feminino
            </GenderTab>
          </GenderTabs>
        </ControlGroup>

        <ControlGroup>
          <Label>Cor</Label>
          <ColorOptions>
            {availableColors.map((color) => (
              <ColorButton
                key={color.id}
                color={color.hex}
                selected={selectedColor === color.id}
                onClick={() => setSelectedColor(color.id)}
                title={color.name}
                style={{
                  border: color.id === 'white' 
                    ? `3px solid ${selectedColor === color.id ? '#3B82F6' : '#d1d5db'}` 
                    : undefined
                }}
              />
            ))}
          </ColorOptions>
        </ControlGroup>

        <CombineButton 
          className="w-full mt-4" 
          onClick={handleCombineAndPreview}
          disabled={!designImageUrl || isCombining || !user}
        >
          <Download className="h-4 w-4 mr-2" />
          {isCombining ? 'Gerando Preview...' : 'Gerar Preview da Imagem'}
        </CombineButton>
      </ProductInfo>

      <CombinedImagePreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        combinedImageUrl={combinedImageUrl}
        productName={currentProduct?.name || ''}
        productColor={selectedColor}
        productGender={selectedGender}
        onSave={handleSaveProduct}
        onEdit={handleEditDesign}
        isSaving={isSaving}
      />

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </PreviewContainer>
  );
};

export default LidoProductPreview;
